import {
  PayRateRequestError,
  type PayRateSource,
  type ResolvedGuardRate,
  type ResolvedPayRates,
} from "@/entities/pay-rate";
import type { DayOfWeek } from "@/entities/shift/model/types";

export const PAY_RATE_SOURCE_LABELS: Record<PayRateSource, string> = {
  Custom: "Custom",
  Authorized: "Authorized",
  SiteDefault: "Site default",
  Compensation: "Compensation",
  None: "No rate",
};

export const notOnPayrollWarning = (name: string): string =>
  `${name} isn't set up for payroll — add their compensation or they won't be paid.`;

export const noPayRateForReadOnly = (name: string): string =>
  `${name} has no pay rate. Ask someone who manages pay rates to set one.`;

export const noDefaultPayRate = (name: string): string =>
  `${name} has no default pay rate. Set one here or they won't be paid for this shift.`;

export const RATES_UNAVAILABLE_MESSAGE =
  "Couldn't load pay rates. The shift will use each guard's default rate.";

export function ratesLoadErrorMessage(error: Error | null): string | null {
  if (!error) return null;
  return error instanceof PayRateRequestError && error.status === 422
    ? error.message
    : RATES_UNAVAILABLE_MESSAGE;
}

export const RATE_INPUT_PATTERN = /^\d{0,3}(\.\d{0,2})?$/;

export interface RateGuard {
  id: number;
  name: string;
  employeeType?: string;
}

interface RosterGuard {
  id: number;
  firstName: string;
  lastName: string;
  employeeType?: string;
}

export function toRateGuards(ids: number[], roster: RosterGuard[] | undefined): RateGuard[] {
  return ids.map((id) => {
    const guard = roster?.find((g) => g.id === id);
    return {
      id,
      name: guard ? `${guard.firstName} ${guard.lastName}` : `Guard ${id}`,
      employeeType: guard?.employeeType,
    };
  });
}

export interface ReviewRates {
  guards: { id: number; name: string; rate: number | null; isCustom: boolean }[];
  billRate: number | null;
  estimate: { pay: number; billing: number | null };
}

export interface GuardRateView {
  rate: number | null;
  source: PayRateSource | null;
}

export function indexResolvedGuards(
  resolved: ResolvedPayRates | undefined,
): Map<number, ResolvedGuardRate> {
  return new Map((resolved?.guards ?? []).map((g) => [g.employeeUserId, g]));
}

export function guardRateView(
  customRate: number | undefined,
  resolved: ResolvedGuardRate | undefined,
): GuardRateView {
  if (customRate !== undefined) return { rate: customRate, source: "Custom" };
  if (resolved) return { rate: resolved.rate, source: resolved.source };
  return { rate: null, source: null };
}

export function shiftHours(
  startDate: string,
  startTime: string,
  endDate: string,
  endTime: string,
): number {
  const start = new Date(`${startDate}T${startTime}`).getTime();
  const end = new Date(`${endDate}T${endTime}`).getTime();
  const hours = (end - start) / 3_600_000;
  return Number.isFinite(hours) && hours > 0 ? hours : 0;
}

export function shiftOccurrences(
  recurrenceDays: DayOfWeek[] | undefined,
  recurrenceWeeks: number | undefined,
): number {
  const days = recurrenceDays?.length ?? 0;
  return days > 0 ? days * (recurrenceWeeks ?? 1) : 1;
}

export function estimateShiftCost(args: {
  hours: number;
  guardRates: (number | null)[];
  billRate: number | null;
}): { pay: number; billing: number | null } {
  const { hours, guardRates, billRate } = args;
  const pay = guardRates.reduce<number>((sum, rate) => sum + (rate ?? 0) * hours, 0);
  const billing = billRate === null ? null : billRate * hours * guardRates.length;
  return { pay, billing };
}

export function buildReviewRates(args: {
  guards: RateGuard[];
  customRates: Record<number, number>;
  resolved: ResolvedPayRates;
  hours: number;
}): ReviewRates {
  const { guards, customRates, resolved, hours } = args;
  const resolvedById = indexResolvedGuards(resolved);
  const rows = guards.map((guard) => {
    const view = guardRateView(customRates[guard.id], resolvedById.get(guard.id));
    return { id: guard.id, name: guard.name, rate: view.rate, isCustom: view.source === "Custom" };
  });
  const estimate = estimateShiftCost({
    hours,
    guardRates: rows.map((row) => row.rate),
    billRate: resolved.billRate,
  });
  return { guards: rows, billRate: resolved.billRate, estimate };
}
